/**
 * The sessions list (TMMAPP-0142 item 2; v2, TMMAPP-0153 items 2–3, visual contract v2 §11): the
 * title "My Financial Assistant" (Newsreader 24), **New conversation**, the search across sessions,
 * then the rows. Archived is a toggle at the foot.
 *
 * - **New conversation** (an outline button, full width) opens a menu: **Private** "Just you and
 *   your assistant" and **Shared** "With the people on a virtual account", each the page's existing
 *   creation. For a GUEST or NONE tier both go through `useSubscriberAction()` (the purchase).
 * - **A row:** a capitals tag with an icon in its tone ("Shared with {names}", "Breakout · private",
 *   "Private" or "Private · {virtual account}", "Help & feedback"; `sessionKind.ts`), the title
 *   (14 / 600), its unread count, and the time (12, through `format.ts`) with the scope summary. The selected row is on
 *   `surface` with a `line` border, radius 12, and is the current row (`aria-current`). The list is
 *   a navigation list.
 * - **Help & feedback** (U2, `kind: SUPPORT`) is pinned first, with no menu: it can't be renamed,
 *   archived or deleted. It's left out when the person has none.
 * - **Top-level conversations only** (TMMAPP-0178 item 4, the Operator's mock-up, superseding visual
 *   contract v2 §11's nested row): a breakout (TMMAPP-0148 U1, `visibility: BREAKOUT`, the person's
 *   own) has no row while its shared session reads (`parentAvailable`); the chat's room tabs reach it,
 *   its header's menu deletes it, and while it's open its shared session's row is the current one. One
 *   whose shared session is gone keeps its own row, "Private room · {name}", with Delete as its one
 *   action (0148 item 5). Another member's room is never listed: no API serves one.
 * - Each row's menu, headed by its title: Rename, Archive or Restore, Delete, for whoever may manage
 *   it (`canManage`: its creator, or a shared session's virtual account owner).
 *
 * **Search across sessions** (TMMAPP-0148 U2 item 13): the field at the top of the list, at every
 * width. While a query runs, its hits replace the rows, grouped by session in the order of each
 * session's newest hit, under the label the page gives (`searchPlace`, which also hides a hit). Each
 * opens `/me/assistant/{id}?message={lineId}`. More reads the next page with the served
 * `nextBeforeId` as `beforeId` (TMMAPP-0149 item 4, TMMAPI-0185 U1), passed back as served; with more
 * hits and no cursor, it asks for a longer query.
 */
import { useCallback, useId, useState, type MouseEvent } from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { assistantApi, type AssistantSearchHit, type AssistantSession } from '@/api/assistant';
import { AddIcon } from '@/components/icons';
import { Pill } from '@/components/Pill';
import { formatCivilDateNear, formatInstant, formatNumber, instantDaysAgo } from '@/i18n/format';
import { todayCivilInZone, utcToZonedCivil } from '@/i18n/zonedCivil';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { SubscriberActionHint, type SubscriberAction } from '@/session/useSubscriberAction';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { resolveTokens } from '@/theme/theme';
import { scopeSummary } from './assistantCopy';
import { hitAuthor } from './search/hitAuthor';
import { SearchField, SearchHits, type HitGroup } from './search/SearchResults';
import { useTranscriptSearch, type CursorOf } from './search/useTranscriptSearch';
import { SessionKindIcon } from './SessionKindIcon';
import {
  sessionKindInk,
  sessionKindOf,
  sessionTagLabel,
  sessionVirtualAccountName,
  type SessionKind,
} from './sessionKind';

export type SessionAction = 'rename' | 'archive' | 'restore' | 'delete';

/** A cross-session hit's session: its label, and whether its messages are other members' too. */
export interface SearchPlace {
  label: string;
  shared: boolean;
}

/** The search across sessions pages by its served, opaque cursor. */
const servedCursor: CursorOf<string> = (page) => page.nextBeforeId;

/** Hits grouped by session, each group placed where its newest hit is (hits come newest first). */
function groupHits(
  hits: readonly AssistantSearchHit[],
  placeOf: (hit: AssistantSearchHit) => SearchPlace | null,
): (HitGroup & { place: SearchPlace })[] {
  const groups = new Map<number, HitGroup & { place: SearchPlace }>();
  for (const hit of hits) {
    if (hit.sessionId === null) continue;
    const group = groups.get(hit.sessionId);
    if (group !== undefined) {
      group.hits.push(hit);
      continue;
    }
    const place = placeOf(hit);
    if (place === null) continue;
    groups.set(hit.sessionId, {
      key: String(hit.sessionId),
      label: place.label,
      hits: [hit],
      place,
    });
  }
  return [...groups.values()];
}

/** A breakout whose shared session reads: reached from that session's tabs, never its own row. */
function inTabs(row: AssistantSession): boolean {
  return row.visibility === 'BREAKOUT' && row.parentAvailable && row.parentSessionId !== null;
}

/** The top-level rows: every session but a breakout reached from its shared session's tabs. */
function topLevel(rows: readonly AssistantSession[]): AssistantSession[] {
  return rows.filter((row) => !inTabs(row));
}

interface SessionListProps {
  headingId: string;
  active: AssistantSession[] | null;
  archived: AssistantSession[] | null;
  showArchived: boolean;
  onShowArchived: (show: boolean) => void;
  selectedId: number | null;
  /** The person's Help & feedback session, pinned first; null when there is none (yet). */
  support?: AssistantSession | null | undefined;
  /** New conversation's two kinds: each opens the existing creation (a guest's, the purchase). */
  newPrivate: SubscriberAction;
  newShared: SubscriberAction;
  onAction: (session: AssistantSession, action: SessionAction) => void;
  /** A row was chosen (the page closes a narrow layout's list). */
  onOpen?: (() => void) | undefined;
  /** Where a search hit is; null hides it. Without it there is no search. */
  searchPlace?: ((hit: AssistantSearchHit) => SearchPlace | null) | undefined;
}

export function SessionList({
  headingId,
  active,
  archived,
  showArchived,
  onShowArchived,
  selectedId,
  support = null,
  newPrivate,
  newShared,
  onAction,
  onOpen,
  searchPlace,
}: SessionListProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const locale = useLocale();
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  const menuId = useId();
  const navLabelId = useId();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const search = useTranscriptSearch(
    useCallback((q: string, beforeId?: string) => assistantApi.searchAll(q, beforeId), []),
    servedCursor,
  );
  const searching = searchPlace !== undefined && search.q !== '';
  // While the person is in a private room, its shared session's row is the current one.
  const openRoom = [...(active ?? []), ...(archived ?? [])].find(
    (row) => row.id === selectedId && inTabs(row),
  );
  const currentId = openRoom?.parentSessionId ?? selectedId;
  const groups = searching && search.hits !== null ? groupHits(search.hits, searchPlace) : [];

  /** The row's time, relative (TMMAPP-0154 item 26): "Today, 8:12 AM", "Yesterday", else the date. */
  function when(iso: string): string {
    try {
      const daysAgo = instantDaysAgo(iso, zone);
      if (daysAgo === 0) {
        return t('assistant.when.today', {
          time: formatInstant(iso, locale, zone, { timeStyle: 'short' }),
        });
      }
      if (daysAgo === 1) return t('assistant.when.yesterday');
      // This year's dates leave the year out ("Oct 9"), in the viewer's zone (TMMAPP-0156 item 2).
      return formatCivilDateNear(utcToZonedCivil(iso, zone).date, locale, todayCivilInZone(zone));
    } catch {
      return iso;
    }
  }

  /** The tag, the title (with its unread count) and the meta line of one row. */
  function rowBody(
    kind: SessionKind,
    tag: string,
    title: string,
    meta: string,
    selected: boolean,
    unread: number,
  ) {
    const ink = sessionKindInk(kind, tokens);
    return (
      <>
        <Box
          component="span"
          data-session-tag={kind}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            maxWidth: '100%',
            fontSize: 11,
            lineHeight: '16px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: ink,
            overflowWrap: 'anywhere',
          }}
        >
          <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>
            <SessionKindIcon kind={kind} size={13} />
          </Box>
          {tag}
        </Box>
        <Box
          component="span"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%', minWidth: 0 }}
        >
          <Typography
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 14,
              lineHeight: '20px',
              fontWeight: 600,
              color: tokens.text,
              overflowWrap: 'anywhere',
            }}
          >
            {title}
          </Typography>
          {unread > 0 && (
            <Box component="span" data-unread={unread}>
              <Pill wash="brand">
                <span aria-hidden>{formatNumber(unread, locale)}</span>
                <Box component="span" sx={visuallyHidden}>
                  {t('assistant.unread', { count: unread })}
                </Box>
              </Pill>
            </Box>
          )}
        </Box>
        <Typography
          component="span"
          data-session-time=""
          sx={{
            fontSize: 12,
            lineHeight: '16px',
            color: tokens.textLabel(selected ? 'surface' : 'bg'),
          }}
        >
          {meta}
        </Typography>
      </>
    );
  }

  const rowButtonSx = (selected: boolean) =>
    ({
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 0.5,
      minWidth: 0,
      p: '10px 12px',
      borderRadius: '12px',
      border: '1px solid',
      borderColor: selected ? tokens.line : 'transparent',
      bgcolor: selected ? tokens.surface : 'transparent',
      '&:hover': { bgcolor: selected ? tokens.surface : tokens.hover },
      // The time is the label ink at rest; on the hovered page it's `text2` (guaranteed there).
      '&:hover [data-session-time]': selected ? {} : { color: tokens.text2 },
      '&.Mui-selected, &.Mui-selected:hover': { bgcolor: tokens.surface },
      '&.Mui-focusVisible': {
        bgcolor: selected ? tokens.surface : tokens.hover,
        outline: `2px solid ${tokens.focusRing}`,
        outlineOffset: 2,
      },
    }) as const;

  function row(session: AssistantSession) {
    const selected = session.id === currentId;
    const archivedRow = session.status === 'ARCHIVED';
    const room = session.visibility === 'BREAKOUT';
    const kind = sessionKindOf(session);
    // A listed breakout is one whose shared session is gone: it names that session.
    const title = room
      ? t('assistant.breakout.row', { parentName: session.parentName ?? session.name })
      : session.name;
    const tag = sessionTagLabel(
      t,
      session,
      locale,
      sessionVirtualAccountName(session, me.virtualAccounts),
    );
    return (
      <Box
        component="li"
        key={session.id}
        data-session-row={session.id}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}
      >
        <ListItemButton
          component={RouterLink}
          to={`/me/assistant/${session.id}`}
          selected={selected}
          aria-current={selected ? 'page' : undefined}
          onClick={onOpen}
          sx={{ flex: 1, ...rowButtonSx(selected) }}
        >
          {rowBody(
            kind,
            tag,
            title,
            // The time, then what it reads (0142 item 2's scope summary, kept on the meta line).
            t('assistant.list.meta', {
              when: when(session.updatedAt),
              scope: scopeSummary(t, session, locale),
            }),
            selected,
            session.unreadCount,
          )}
        </ListItemButton>
        {session.canManage && (
          <Box sx={{ pt: 0.75 }}>
            <OverflowMenu title={title}>
              {(close) => [
                ...(room
                  ? []
                  : [
                      <MenuItem
                        key="rename"
                        onClick={() => {
                          close();
                          onAction(session, 'rename');
                        }}
                      >
                        {t('assistant.menu.rename')}
                      </MenuItem>,
                      <MenuItem
                        key="archive"
                        onClick={() => {
                          close();
                          onAction(session, archivedRow ? 'restore' : 'archive');
                        }}
                      >
                        {t(archivedRow ? 'assistant.menu.restore' : 'assistant.menu.archive')}
                      </MenuItem>,
                    ]),
                <MenuItem
                  key="delete"
                  onClick={() => {
                    close();
                    onAction(session, 'delete');
                  }}
                >
                  {t('assistant.menu.delete')}
                </MenuItem>,
              ]}
            </OverflowMenu>
          </Box>
        )}
      </Box>
    );
  }

  function supportRow(target: AssistantSession) {
    const selected = target.id === selectedId;
    return (
      <Box component="li" key="support" data-session-row={target.id} data-support-row="">
        <ListItemButton
          component={RouterLink}
          to={`/me/assistant/${target.id}`}
          selected={selected}
          aria-current={selected ? 'page' : undefined}
          onClick={onOpen}
          sx={rowButtonSx(selected)}
        >
          {rowBody(
            'support',
            t('assistant.tag.support'),
            t('support.rowTitle'),
            t('support.rowAlways'),
            selected,
            0,
          )}
        </ListItemButton>
      </Box>
    );
  }

  const listSx = { listStyle: 'none', m: 0, display: 'flex', flexDirection: 'column', gap: 0.5 };

  function choose(action: SubscriberAction, event: MouseEvent<HTMLElement>) {
    setMenuAnchor(null);
    action.run(event as MouseEvent<HTMLButtonElement>);
  }

  return (
    <Stack sx={{ minHeight: 0, height: '100%' }}>
      <Stack sx={{ gap: 1.75, p: '22px 16px 12px' }}>
        <Typography
          id={headingId}
          tabIndex={-1}
          component="h2"
          sx={{
            m: 0,
            px: 0.75,
            fontFamily: (current) => current.typography.h4.fontFamily,
            fontWeight: 400,
            fontSize: 24,
            lineHeight: 1.2,
            color: tokens.text,
          }}
        >
          {t('nav.assistant')}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          fullWidth
          startIcon={<AddIcon />}
          aria-haspopup="menu"
          aria-expanded={menuAnchor !== null}
          aria-controls={menuAnchor !== null ? menuId : undefined}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
          data-new-conversation=""
          sx={{
            borderRadius: '12px',
            py: 1.25,
            // A `brand` outline with `brand` text (TMMAPP-0154 item 26): the label is `link` (brand
            // text, guaranteed 4.5:1) and the border `brandBoundary` (3:1 on the list's canvas).
            color: tokens.link,
            borderColor: tokens.brandBoundary,
            '&:hover': { borderColor: tokens.brandBoundary },
            // Contract §11: sentence case, 14 / 600, as drawn.
            textTransform: 'none',
            letterSpacing: 0,
            fontWeight: 600,
          }}
        >
          {t('assistant.newConversation')}
        </Button>
        <Menu
          id={menuId}
          anchorEl={menuAnchor}
          open={menuAnchor !== null}
          onClose={() => setMenuAnchor(null)}
          slotProps={{
            list: { 'aria-label': t('assistant.newMenu.label') },
            paper: { sx: { minWidth: menuAnchor?.offsetWidth ?? undefined } },
          }}
        >
          {(
            [
              ['private', newPrivate] as const,
              ['shared', newShared] as const,
            ] satisfies readonly (readonly [string, SubscriberAction])[]
          ).map(([key, action]) => (
            <MenuItem
              key={key}
              onClick={(event) => choose(action, event)}
              aria-describedby={action.descriptionId}
              data-new-kind={key}
              sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.25, py: 1.25 }}
            >
              <Box component="span" sx={{ fontSize: 14, fontWeight: 600, color: tokens.text }}>
                {t(key === 'private' ? 'assistant.newMenu.private' : 'assistant.newMenu.shared')}
              </Box>
              <Box
                component="span"
                sx={{
                  fontSize: 12,
                  color: tokens.textLabel('surfaceRaised'),
                  whiteSpace: 'normal',
                }}
              >
                {t(
                  key === 'private'
                    ? 'assistant.newMenu.privateHelp'
                    : 'assistant.newMenu.sharedHelp',
                )}
              </Box>
            </MenuItem>
          ))}
        </Menu>
        <SubscriberActionHint id={newPrivate.descriptionId} description={newPrivate.description} />
        <SubscriberActionHint id={newShared.descriptionId} description={newShared.description} />
        {searchPlace !== undefined && (
          <Box data-sessions-search="">
            <SearchField
              search={search}
              label={t('assistant.search.fieldAll')}
              // The column is narrow: a short placeholder; the full wording is the field's name.
              placeholder={t('assistant.search.fieldAllShort')}
            />
          </Box>
        )}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pb: 2 }}>
        {searching && (
          <Box sx={{ py: 1 }}>
            <SearchHits
              search={search}
              groups={groups}
              whoOf={(hit) =>
                hitAuthor(
                  t,
                  hit.role,
                  groups.find((group) => group.key === String(hit.sessionId))?.place.shared ?? true,
                )
              }
              hrefOf={(hit) => `/me/assistant/${hit.sessionId ?? ''}?message=${hit.id}`}
              onOpen={onOpen}
              moreNote={t('assistant.search.moreAll')}
            />
          </Box>
        )}
        {!searching && (
          <Box component="nav" aria-labelledby={navLabelId} data-session-nav="">
            <Box component="span" id={navLabelId} sx={visuallyHidden}>
              {t('assistant.list.nav')}
            </Box>
            {support !== null && (
              <List component="ul" disablePadding sx={{ ...listSx, mb: 0.5 }} data-pinned="">
                {supportRow(support)}
              </List>
            )}
            {active === null ? (
              <Typography sx={{ p: 1.5, fontSize: 14, color: tokens.text2 }}>
                {t('common.loading')}
              </Typography>
            ) : topLevel(active).length === 0 ? (
              <Typography sx={{ p: 1.5, fontSize: 14, color: tokens.text2 }} data-sessions-empty="">
                {t('assistant.emptySessions')}
              </Typography>
            ) : (
              <List component="ul" disablePadding sx={listSx}>
                {topLevel(active).map(row)}
              </List>
            )}
            {showArchived && (
              <Box data-archived-sessions="">
                <Typography
                  component="h3"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: tokens.textLabel('bg'),
                    px: 1.5,
                    pt: 2,
                    pb: 1,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                  }}
                >
                  {t('assistant.archivedHeading')}
                </Typography>
                {archived === null ? (
                  <Typography sx={{ px: 1.5, pb: 2, fontSize: 14, color: tokens.text2 }}>
                    {t('common.loading')}
                  </Typography>
                ) : topLevel(archived).length === 0 ? (
                  <Typography sx={{ px: 1.5, pb: 2, fontSize: 14, color: tokens.text2 }}>
                    {t('assistant.archivedEmpty')}
                  </Typography>
                ) : (
                  <List component="ul" disablePadding sx={listSx}>
                    {topLevel(archived).map(row)}
                  </List>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
      <Box sx={{ px: 2.5, py: 1, borderTop: `1px solid ${tokens.line}` }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showArchived}
              onChange={(event) => onShowArchived(event.target.checked)}
            />
          }
          label={<Typography sx={{ fontSize: 13 }}>{t('assistant.showArchived')}</Typography>}
        />
      </Box>
    </Stack>
  );
}
